// Phase 2 P2-4 — useSignalSearch hook (MUST BUILD Signal Window).
//
// Wraps the signal-search Supabase Edge function with three actions:
//   create — new query (window_kind, target_odds, max_legs, ...)
//   refine — adjustment command on an existing query
//   get    — fetch a persisted query by id
//
// State machine:
//   idle → submitting → result | error
//   result → refining → result | error
//
// All calls route through supabase.functions.invoke() which handles
// auth headers automatically (anon JWT → authenticated upgrade once
// the user is signed in).

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type WindowKind = "today" | "tomorrow" | "weekend" | "7d" | "month" | "custom";

// Wire shape AFTER the signal-search sanitiser — engine internals
// (composite_score, desk_pick_count, raw best_prob, has_high_clv) are
// stripped server-side. The client only ever sees outputs + a coarse
// confidence band.
export interface SignalLeg {
  fixture_id: string;
  home_team: string;
  away_team: string;
  competition: string;
  kickoff_at: string;
  market: string;
  selection: string;
  odds: number;
  confidence: "high" | "moderate";
  reason: string;
}

export interface ComposedSlip {
  legs: SignalLeg[];
  combined_odds: number;
  estimated_probability: number;
  leg_count: number;
  avg_leg_odds: number;
  risk_notes: string[];
}

export interface SignalSearchResult {
  query_id?: string;
  window: { kind: string; start: string; end: string };
  target_odds: number;
  slip: ComposedSlip | null;
  alternatives: { safer?: ComposedSlip; aggressive?: ComposedSlip };
  candidates_summary: {
    total_scanned: number;
    rejected: number;
    qualified: number;
    excluded_by_user: string[] | null;
  };
  candidate_pool: SignalLeg[];
  notes: string[];
  /** Ordered, reversible refinement commands applied to this query. The
   *  server is the source of truth (undo drops one and recomposes). */
  command_stack?: string[];
  refinement_log?: { command: string; stack?: string[]; parsed?: unknown; applied?: string[] };
}

export interface CreateInput {
  window_kind: WindowKind;
  target_odds: number;
  max_legs?: number;
  exclude_leagues?: string[];
  sport?: string;
  start_date?: string;
  end_date?: string;
}

export interface UseSignalSearchState {
  result: SignalSearchResult | null;
  loading: boolean;
  refining: boolean;
  error: string | null;
  /** Free-text refinement commands the user has issued, newest first. */
  refinementHistory: string[];
  create: (input: CreateInput) => Promise<void>;
  refine: (command: string) => Promise<void>;
  reset: () => void;
}

export function useSignalSearch(): UseSignalSearchState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [result, setResult] = useState<SignalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refinementHistory, setRefinementHistory] = useState<string[]>([]);

  const create = useCallback(
    async (input: CreateInput): Promise<void> => {
      setLoading(true);
      setError(null);
      setRefinementHistory([]);
      try {
        const body = { action: "create", user_id: user?.id ?? null, ...input };
        const { data, error: invokeErr } = await supabase.functions.invoke<SignalSearchResult>(
          "signal-search",
          { body },
        );
        if (invokeErr) {
          setError(invokeErr.message);
          setResult(null);
        } else {
          setResult(data);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setResult(null);
      } finally {
        setLoading(false);
      }
    },
    [supabase, user],
  );

  const refine = useCallback(
    async (command: string): Promise<void> => {
      if (!result?.query_id) {
        setError("No query to refine. Run a search first.");
        return;
      }
      setRefining(true);
      setError(null);
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke<SignalSearchResult>(
          "signal-search",
          { body: { action: "refine", query_id: result.query_id, command } },
        );
        if (invokeErr) {
          setError(invokeErr.message);
        } else {
          setResult(data);
          setRefinementHistory((prev) => [command, ...prev]);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setRefining(false);
      }
    },
    [supabase, result?.query_id],
  );

  const reset = useCallback((): void => {
    setResult(null);
    setError(null);
    setRefinementHistory([]);
  }, []);

  return useMemo<UseSignalSearchState>(
    () => ({ result, loading, refining, error, refinementHistory, create, refine, reset }),
    [result, loading, refining, error, refinementHistory, create, refine, reset],
  );
}
